/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
  styled
} from '@mui/material';
import React, { useCallback, useMemo, useRef } from 'react';
import { TABS, useInjection } from './hook';
import { FormOption } from './components/FormOption';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import ModalNewDropdown from './components/ModalNewDropdown';
import ModalNewSingleField from './components/ModalNewSingleField';
import { headerTabOptions } from '../ValidationsPage/const';
import { TabsCustom } from 'app/components/elements/TabsCutom';
import { TableCustom } from 'app/components/elements/TableCustom';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { InputText } from 'app/components/elements/InputText';
import type { IssuesUiConfig } from 'utils/issueDueDateColor';
import type { CriticalityLevel } from 'utils/issueCriticality';
import { DEFAULT_CRITICALITY_LEVELS } from 'utils/issueCriticality';

const COLOR_TOKEN_FALLBACK: Record<string, string> = {
  'error.main': '#dc2626',
  'warning.main': '#ea580c',
  'success.main': '#16a34a',
  'text.secondary': '#6b7280'
};

const toPickerColor = (value?: string) => {
  if (!value) return '#6b7280';
  if (value.startsWith('#')) return value;
  return COLOR_TOKEN_FALLBACK[value] || '#6b7280';
};

type IssuesConfigColorKey =
  | 'pastDueColor'
  | 'nearNearDueColor'
  | 'nearDueColor'
  | 'normalDueColor'
  | 'inactiveColor';

type IssuesConfigSectionProps = {
  title: string;
  subtitle?: string;
  description?: string;
  accentColor?: string;
  children: React.ReactNode;
};

const IssuesConfigSection = ({
  title,
  subtitle,
  description,
  accentColor = '#226AAA',
  children
}: IssuesConfigSectionProps) => (
  <IssuesConfigSectionCard>
    <IssuesConfigSectionHeader accentColor={accentColor}>
      <Typography fontWeight={700} fontSize={18} sx={{ color: accentColor }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography fontWeight={600} fontSize={15} mt={0.75}>
          {subtitle}
        </Typography>
      ) : null}
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 900 }}>
          {description}
        </Typography>
      ) : null}
    </IssuesConfigSectionHeader>
    <IssuesConfigSectionBody>{children}</IssuesConfigSectionBody>
  </IssuesConfigSectionCard>
);

export const HEADER_FIELD = [
  { label: 'Name', width: 200 },
  { label: 'Type', width: 100 },
  { label: 'Entries' },
  { label: 'Clients' },
  { label: 'Required', width: 100 },
  { label: 'Actions', width: 100 }
];
/**
 *
 * @constructor
 */
export const OptionsPage = () => {
  const {
    listOptions,
    isOpenModalDropdown,
    singleFields,
    clientOptions,
    onToggleModalDropdown,
    handleCreateNewOption,
    onDeleteDropdown,
    handleCreateDropdown,
    handleCreateSingleField,
    handleUpdateSingleField,
    handleDeleteSingleField,
    isOpenModalSingleField,
    setIsOpenModalSingleField,
    selectedSingleFields,
    setSelectedSingleFields,
    tab,
    setTab,
    issuesConfig,
    setIssuesConfig,
    issuesConfigLoading,
    handleSaveIssuesConfig
  } = useInjection();

  // Color picker drag fires many events; throttle updates to animation frames
  // to avoid re-rendering the whole page for each micro-movement.
  const rafIdRef = useRef<number | null>(null);
  const pendingColorRef = useRef<{
    key: IssuesConfigColorKey;
    value: string;
  } | null>(null);

  const flushPendingColorUpdate = useCallback(() => {
    if (!pendingColorRef.current) return;
    const { key, value } = pendingColorRef.current;
    pendingColorRef.current = null;
    setIssuesConfig((prev: IssuesUiConfig) => ({
      ...prev,
      [key]: value
    }));
  }, [setIssuesConfig]);

  const scheduleColorUpdate = useCallback(
    (key: IssuesConfigColorKey, value: string) => {
      pendingColorRef.current = { key, value };
      if (rafIdRef.current != null) return;
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        flushPendingColorUpdate();
      });
    },
    [flushPendingColorUpdate]
  );

  const renderColorPickerAdornment = useCallback(
    (
      colorValue: string | undefined,
      onChange: (hex: string) => void,
      options?: { onBlur?: () => void; onPointerUp?: () => void }
    ) => (
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 0.5,
          flexShrink: 0
        }}>
        <ColorPickerInput
          type="color"
          value={toPickerColor(colorValue)}
          onChange={(e) => onChange(e.target.value)}
          onBlur={options?.onBlur}
          onPointerUp={options?.onPointerUp}
          aria-label="Pick color"
        />
      </Box>
    ),
    []
  );

  const renderIssuesConfigColorField = useCallback(
    (
      title: string,
      placeholder: string,
      textValue: string,
      configKey: IssuesConfigColorKey,
      onTextChange: (value: string) => void
    ) => (
      <InputText
        title={title}
        placeholder={placeholder}
        inputProps={{
          value: textValue,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => onTextChange(e.target?.value ?? '')
        }}
        endAdornment={renderColorPickerAdornment(
          textValue,
          (hex) => scheduleColorUpdate(configKey, hex),
          {
            onBlur: flushPendingColorUpdate,
            onPointerUp: flushPendingColorUpdate
          }
        )}
      />
    ),
    [flushPendingColorUpdate, renderColorPickerAdornment, scheduleColorUpdate]
  );

  const renderTab = useCallback(() => {
    if (tab === TABS.DROPDOWN) {
      return (
        <>
          {listOptions && (
            <Grid container spacing={3} mt={1} justifyContent="flex-start" alignItems="center">
              {listOptions.map((option) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={option.name}>
                  <FormOption
                    onsubmit={handleCreateNewOption}
                    title={option.name}
                    id={option.id}
                    required={option.required}
                    type={option.options.map((item) => item.type)}
                    onDeleteDropdown={onDeleteDropdown}
                    options={option.dropdownValues}
                    clientIds={option.dropdownOptionClients?.map((c) => c.clientId)}
                    clientOptions={clientOptions}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      );
    }
    if (tab === TABS.ISSUES_CONFIG) {
      const criticalityLevels = issuesConfig.criticalityLevels?.length
        ? issuesConfig.criticalityLevels
        : DEFAULT_CRITICALITY_LEVELS;

      return (
        <Stack gap={3} mt={2} width="100%">
          <IssuesConfigSection
            title="Color settings"
            subtitle="Issue due date appearance"
            description="Configure day thresholds and colors used for Ad Hoc issue due-date states in Admin and mobile apps."
            accentColor="#226AAA">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputText
                  title="Urgent (days remaining)"
                  placeholder="7"
                  inputProps={{
                    value: String(issuesConfig.nearNearDueDays ?? ''),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      setIssuesConfig((prev: IssuesUiConfig) => ({
                        ...prev,
                        nearNearDueDays: Number(e.target?.value ?? 0)
                      }))
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputText
                  title="Due soon (days remaining)"
                  placeholder="14"
                  inputProps={{
                    value: String(issuesConfig.nearDueDays ?? ''),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      setIssuesConfig((prev: IssuesUiConfig) => ({
                        ...prev,
                        nearDueDays: Number(e.target?.value ?? 0)
                      }))
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6} md={4}>
                {renderIssuesConfigColorField(
                  'Overdue',
                  '#dc2626',
                  issuesConfig.pastDueColor || '',
                  'pastDueColor',
                  (value) =>
                    setIssuesConfig((prev: IssuesUiConfig) => ({ ...prev, pastDueColor: value }))
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderIssuesConfigColorField(
                  'Urgent',
                  '#f59e0b',
                  issuesConfig.nearNearDueColor || '',
                  'nearNearDueColor',
                  (value) =>
                    setIssuesConfig((prev: IssuesUiConfig) => ({
                      ...prev,
                      nearNearDueColor: value
                    }))
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderIssuesConfigColorField(
                  'Due soon',
                  '#ea580c',
                  issuesConfig.nearDueColor || '',
                  'nearDueColor',
                  (value) =>
                    setIssuesConfig((prev: IssuesUiConfig) => ({ ...prev, nearDueColor: value }))
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderIssuesConfigColorField(
                  'On time',
                  '#16a34a',
                  issuesConfig.normalDueColor || '',
                  'normalDueColor',
                  (value) =>
                    setIssuesConfig((prev: IssuesUiConfig) => ({ ...prev, normalDueColor: value }))
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderIssuesConfigColorField(
                  'Resolved/archived',
                  '#6b7280',
                  issuesConfig.inactiveColor || '',
                  'inactiveColor',
                  (value) =>
                    setIssuesConfig((prev: IssuesUiConfig) => ({ ...prev, inactiveColor: value }))
                )}
              </Grid>
            </Grid>
          </IssuesConfigSection>

          <IssuesConfigSection
            title="Criticality levels"
            description="Configure severity levels for room, asset, and ad hoc issues. Keys must be unique."
            accentColor="#7c3aed">
            <Grid container spacing={2}>
              {criticalityLevels.map(
                (level: CriticalityLevel, index: number, arr: CriticalityLevel[]) => (
                  <Grid item xs={12} key={`${level.key}-${index}`}>
                    <Stack
                      gap={1.5}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 1,
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: '#fafafa'
                      }}>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} sm={6} md={4}>
                          <InputText
                            title="Label"
                            placeholder="High"
                            inputProps={{
                              value: level.label,
                              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                                setIssuesConfig((prev: IssuesUiConfig) => {
                                  const levels = [...(prev.criticalityLevels || [])];
                                  levels[index] = { ...levels[index], label: e.target.value };
                                  return { ...prev, criticalityLevels: levels };
                                })
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <InputText
                            title="Key"
                            placeholder="high"
                            inputProps={{
                              value: level.key,
                              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                                setIssuesConfig((prev: IssuesUiConfig) => {
                                  const levels = [...(prev.criticalityLevels || [])];
                                  levels[index] = {
                                    ...levels[index],
                                    key: e.target.value.trim().toLowerCase().replace(/\s+/g, '_')
                                  };
                                  return { ...prev, criticalityLevels: levels };
                                })
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <InputText
                            title="Color token or hex"
                            placeholder="error.main"
                            inputProps={{
                              value: level.color,
                              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                                setIssuesConfig((prev: IssuesUiConfig) => {
                                  const levels = [...(prev.criticalityLevels || [])];
                                  levels[index] = { ...levels[index], color: e.target.value };
                                  return { ...prev, criticalityLevels: levels };
                                })
                            }}
                            endAdornment={renderColorPickerAdornment(level.color, (hex) =>
                              setIssuesConfig((prev: IssuesUiConfig) => {
                                const levels = [...(prev.criticalityLevels || [])];
                                levels[index] = { ...levels[index], color: hex };
                                return { ...prev, criticalityLevels: levels };
                              })
                            )}
                          />
                        </Grid>
                      </Grid>
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        <Button
                          size="small"
                          disabled={index === 0}
                          onClick={() =>
                            setIssuesConfig((prev: IssuesUiConfig) => {
                              const levels = [...(prev.criticalityLevels || [])];
                              const tmp = levels[index - 1];
                              levels[index - 1] = { ...levels[index], sortOrder: index - 1 };
                              levels[index] = { ...tmp, sortOrder: index };
                              return {
                                ...prev,
                                criticalityLevels: levels.map((l, i) => ({ ...l, sortOrder: i }))
                              };
                            })
                          }>
                          Move up
                        </Button>
                        <Button
                          size="small"
                          disabled={index >= arr.length - 1}
                          onClick={() =>
                            setIssuesConfig((prev: IssuesUiConfig) => {
                              const levels = [...(prev.criticalityLevels || [])];
                              const tmp = levels[index + 1];
                              levels[index + 1] = { ...levels[index], sortOrder: index + 1 };
                              levels[index] = { ...tmp, sortOrder: index };
                              return {
                                ...prev,
                                criticalityLevels: levels.map((l, i) => ({ ...l, sortOrder: i }))
                              };
                            })
                          }>
                          Move down
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={arr.length <= 1}
                          onClick={() =>
                            setIssuesConfig((prev: IssuesUiConfig) => {
                              const levels = (prev.criticalityLevels || []).filter(
                                (_, i) => i !== index
                              );
                              return {
                                ...prev,
                                criticalityLevels: levels.map((l, i) => ({ ...l, sortOrder: i }))
                              };
                            })
                          }>
                          Remove
                        </Button>
                      </Stack>
                    </Stack>
                  </Grid>
                )
              )}
            </Grid>

            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() =>
                setIssuesConfig((prev: IssuesUiConfig) => {
                  const levels = [...(prev.criticalityLevels || DEFAULT_CRITICALITY_LEVELS)];
                  const nextIndex = levels.length;
                  levels.push({
                    key: `level_${nextIndex + 1}`,
                    label: `Level ${nextIndex + 1}`,
                    color: 'text.secondary',
                    sortOrder: nextIndex
                  });
                  return { ...prev, criticalityLevels: levels };
                })
              }>
              Add level
            </Button>
          </IssuesConfigSection>

          <IssuesConfigSection
            title="Notifications"
            description="Turn off automatic notifications to stop emails, SMS, and push for system events (new issue, assignment, session completed, duplicate warning) across room, asset, and ad hoc issues. Manual notifications from issue dialogs still send, but only through channels enabled below."
            accentColor="#0891b2">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={issuesConfig.issueAutomaticNotificationsEnabled}
                      onChange={(e) =>
                        setIssuesConfig((prev: IssuesUiConfig) => ({
                          ...prev,
                          issueAutomaticNotificationsEnabled: e.target.checked,
                          adHocAutomaticNotificationsEnabled: e.target.checked
                        }))
                      }
                    />
                  }
                  label="Automatic room, asset, and ad hoc issue notifications"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4 }}>
                  Channels used for both automatic and manual notifications (manual respects your
                  intersected with these toggles).
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={issuesConfig.notifyChannelPush}
                      onChange={(e) =>
                        setIssuesConfig((prev: IssuesUiConfig) => ({
                          ...prev,
                          notifyChannelPush: e.target.checked
                        }))
                      }
                    />
                  }
                  label="Push notifications"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={issuesConfig.notifyChannelEmail}
                      onChange={(e) =>
                        setIssuesConfig((prev: IssuesUiConfig) => ({
                          ...prev,
                          notifyChannelEmail: e.target.checked
                        }))
                      }
                    />
                  }
                  label="Email"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={issuesConfig.notifyChannelSms}
                      onChange={(e) =>
                        setIssuesConfig((prev: IssuesUiConfig) => ({
                          ...prev,
                          notifyChannelSms: e.target.checked
                        }))
                      }
                    />
                  }
                  label="SMS (requires user phone in E.164, e.g. +15551234567)"
                />
              </Grid>
            </Grid>
          </IssuesConfigSection>

          <Stack direction="row" justifyContent="flex-end" pt={1}>
            <ButtonCustom
              loading={issuesConfigLoading}
              style={{ display: 'inline-block', width: 'auto' }}
              onClick={handleSaveIssuesConfig}
              label="Save"
            />
          </Stack>
        </Stack>
      );
    }
    return (
      <TableCustom
        data={singleFields.map((field) => ({
          name: {
            value: <TextEllipsis text={field.name} width={'95%'} />
          },
          type: {
            value: <TextEllipsis text={field.type} width={'95%'} />
          },
          entries: {
            value: (
              <TextEllipsis
                line={2}
                text={
                  Array.isArray(field.entries)
                    ? field.entries.join(', ')
                    : typeof field.entries === 'object' && field.entries !== null
                      ? JSON.stringify(field.entries)
                      : String(field.entries ?? '')
                }
                width={'95%'}
              />
            )
          },
          clients: {
            value: (
              <TextEllipsis
                line={2}
                text={
                  field.singleDataEntryClients?.length
                    ? field.singleDataEntryClients.map((c) => c.client.clientName).join(', ')
                    : ''
                }
                width={'95%'}
              />
            )
          },
          required: {
            value: <TextEllipsis text={field.required ? 'Yes' : 'No'} width={'95%'} />
          },
          actions: {
            value: (
              <Stack flexDirection={'row'} gap={'10px'} alignItems={'center'}>
                <img
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setIsOpenModalSingleField(true);
                    setSelectedSingleFields({
                      ...field,
                      clientIds: field.singleDataEntryClients?.map((c) => c.clientId)
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } as any);
                  }}
                  src={'/icons/icon_action_edit.svg'}
                  alt={`icon_action_edit`}
                />
                <img
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleDeleteSingleField(field.id)}
                  src={'/icons/icon_action_delete.svg'}
                  alt={`icon_action_delete`}
                />
              </Stack>
            )
          }
        }))}
        headers={HEADER_FIELD}
        handleChooseRow={() => {}}
        styledTableRow={{ cursor: ' default ' }}
      />
    );
  }, [
    tab,
    singleFields,
    listOptions,
    handleCreateNewOption,
    onDeleteDropdown,
    clientOptions,
    setIsOpenModalSingleField,
    setSelectedSingleFields,
    handleDeleteSingleField,
    issuesConfig,
    setIssuesConfig,
    issuesConfigLoading,
    handleSaveIssuesConfig,
    renderColorPickerAdornment,
    renderIssuesConfigColorField
  ]);
  return (
    <WrapperPage>
      <ModalNewDropdown
        open={isOpenModalDropdown}
        onClose={onToggleModalDropdown}
        onSubmit={handleCreateDropdown}
        clientOptions={clientOptions}
      />
      <ModalNewSingleField
        defaultValue={selectedSingleFields}
        open={isOpenModalSingleField}
        onClose={() => setIsOpenModalSingleField(false)}
        onCreate={handleCreateSingleField}
        onUpdate={handleUpdateSingleField}
        clientOptions={clientOptions}
      />
      {tab !== TABS.ISSUES_CONFIG && (
        <Stack sx={{ justifyContent: 'flex-end', marginBottom: 5 }} direction="row" spacing={2}>
          <ButtonCustom
            style={{ display: 'inline-block', width: 'auto' }}
            onClick={() => setIsOpenModalSingleField(true)}
            label="Create single entry field"
          />
          <ButtonCustom
            style={{ display: 'inline-block', width: 'auto' }}
            onClick={onToggleModalDropdown}
            label="Create new dropdown"
          />
        </Stack>
      )}
      <TabsCustom
        contentTab={renderTab()}
        tab={tab}
        headerTab={headerTabOptions}
        onChangeTab={setTab}
      />
    </WrapperPage>
  );
};
const WrapperPage = styled(Stack)(() => ({
  padding: '40px 30px 40px 40px',
  width: '100%',
  minHeight: '100vh'
}));

const IssuesConfigSectionCard = styled(Box)({
  width: '100%',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: '#fff',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)'
});

const IssuesConfigSectionHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accentColor'
})<{ accentColor: string }>(({ accentColor }) => ({
  padding: '16px 20px',
  borderBottom: '1px solid #e5e7eb',
  borderLeft: `4px solid ${accentColor}`,
  backgroundColor: '#f8fafc'
}));

const IssuesConfigSectionBody = styled(Box)({
  padding: '20px'
});

/** Color swatch aligned inside InputText end adornment (matches 56px field height). */
const ColorPickerInput = styled('input')({
  width: 36,
  height: 36,
  padding: 0,
  margin: 0,
  border: '1px solid #d1d5db',
  borderRadius: 6,
  cursor: 'pointer',
  backgroundColor: 'transparent',
  flexShrink: 0,
  '&::-webkit-color-swatch-wrapper': {
    padding: 2
  },
  '&::-webkit-color-swatch': {
    border: 'none',
    borderRadius: 4
  }
});
